import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAIAssistant } from '../../contexts/AIAssistantContext';
import { X, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import Dropdown from '../common/Dropdown';
import { Button } from '../common/Button';
import { ButtonGroup } from '../common/ButtonGroup';
import { Badge } from '../common/Badge';
import { API_BASE_URL } from '../../config/api';

import { logger, getErrorMessage } from '../../utils/logger';
/**
 * Global AI Assistant Modal
 * 
 * Features:
 * - Lazy loading (blur screen → "Use AI" button → active UI)
 * - Optimized 1-request flow (analyze + modify in single request)
 * - Provider selection using global Dropdown component
 * - Knowledge base selection using global Dropdown component
 * - Real-time usage tracking from provider APIs
 */

interface KnowledgeBase {
  id: string | null;
  name: string;
  filename?: string;
}

interface Usage {
  date: string;
  providers: {
    [key: string]: {
      requests: number;
      errors: number;
      quotaExceeded: boolean;
    };
  };
}

interface AIResponse {
  analysis: string;
  modifications: {
    updatedScript: string;
    changedLines: number[];
    explanation: string;
  };
  usage: Usage;
}

interface GlobalAIAssistantProps {
  mode?: 'modal' | 'embedded';
  width?: string;
  context?: any;
  onUpdate?: (script: string) => void;
  onStepsUpdate?: (steps: any[]) => void;  // NEW: Callback for pre-parsed steps
  onDescriptionUpdate?: (description: string) => void;  // ✅ NEW: Callback for AI description
}

export const GlobalAIAssistant: React.FC<GlobalAIAssistantProps> = ({ 
  mode = 'modal', 
  width = '100%',
  context: externalContext,
  onUpdate: externalOnUpdate,
  onStepsUpdate: externalOnStepsUpdate,  // NEW: Accept steps update callback
  onDescriptionUpdate: externalOnDescriptionUpdate  // ✅ NEW: Accept description callback
}) => {
  const { isOpen, context: hookContext, provider, knowledgeBase, closeAI, setProvider, setKnowledgeBase } = useAIAssistant();
  
  // Use external context if embedded, otherwise use hook context
  const context = mode === 'embedded' ? externalContext : hookContext;
  const onUpdate = mode === 'embedded' ? externalOnUpdate : hookContext?.onUpdate;
  const onStepsUpdate = mode === 'embedded' ? externalOnStepsUpdate : hookContext?.onStepsUpdate;
  const onDescriptionUpdate = mode === 'embedded' ? externalOnDescriptionUpdate : hookContext?.onDescriptionUpdate;  // ✅ NEW  // NEW
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [userRequest, setUserRequest] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isParsing, setIsParsing] = useState(false);  // NEW: Track parsing state
  const [, setAiResponse] = useState<AIResponse | null>(null);
  const [responseHistory, setResponseHistory] = useState<AIResponse[]>([]); // Conversation history
  const [error, setError] = useState<string | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [kbLoadError, setKbLoadError] = useState<string | null>(null); // NEW: KB load error state
  const [isLoadingKBs, setIsLoadingKBs] = useState(false); // NEW: KB loading state
  const [availableProviders, setAvailableProviders] = useState<any[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [pendingModification, setPendingModification] = useState<{
    script: string;
    explanation: string;
    changedLines: number[];
  } | null>(null);

  const fetchKnowledgeBases = async () => {
    try {
      setIsLoadingKBs(true);
      setKbLoadError(null); // Clear previous errors
      
      const response = await fetch(`${API_BASE_URL}/api/ai/knowledge-bases`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Check HTTP status first
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to access knowledge bases.');
        } else if (response.status === 503) {
          throw new Error('AI service is temporarily unavailable.');
        } else {
          throw new Error(`Failed to load knowledge bases (${response.status})`);
        }
      }
      
      const data = await response.json();
      
      if (data.success) {
        setKnowledgeBases([
          { id: null, name: 'None' },
          ...(data.knowledgeBases || [])
        ]);
        setKbLoadError(null);
      } else {
        // Backend returned success: false
        throw new Error(data.error || 'Failed to load knowledge bases');
      }
    } catch (error) {
      logger.error('Failed to load knowledge bases:', { error: getErrorMessage(error) });
      const errorMsg = error instanceof Error ? error.message : 'Network error loading knowledge bases';
      setKbLoadError(errorMsg);
      
      // Set to empty (with "None" option) on error so dropdown still works
      setKnowledgeBases([{ id: null, name: 'None' }]);
    } finally {
      setIsLoadingKBs(false);
    }
  };

  const fetchAvailableProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/available-models`);
      const data = await response.json();
      
      if (data.success) {
        setAvailableProviders(data.models || []);
        // Set default to unlimited model or first available
        const defaultModel = data.models.find((m: any) => m.label.includes('UNLIMITED')) || data.models[0];
        if (defaultModel && !provider) {
          setProvider(defaultModel.value);
        }
      }
    } catch (error) {
      logger.error('Failed to load available models:', { error: getErrorMessage(error) });
    }
  };

  const handleUseAI = async () => {
    if (isLoadingProviders) return; // Prevent duplicate calls
    
    setIsInitializing(true);
    setIsLoadingProviders(true);
    setError(null);

    try {
      // Fetch providers and knowledge bases only when user clicks "Use AI"
      await Promise.all([
        fetchKnowledgeBases(),
        fetchAvailableProviders()
      ]);

      setIsInitialized(true);
    } catch (error) {
      logger.error('Failed to initialize AI:', { error: getErrorMessage(error) });
      setError('Failed to initialize AI assistant');
    } finally {
      setIsInitializing(false);
      setIsLoadingProviders(false);
    }
  };

  const handleActivateAI = async () => {
    if (!userRequest.trim()) {
      setError('Please describe what you want to modify');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Find selected model details
      const selectedModel = availableProviders.find(m => m.value === provider);
      
      const response = await fetch(`${API_BASE_URL}/api/ai/analyze-and-modify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          provider: selectedModel?.providerId || provider, // Send provider ID
          context,
          userRequest,
          knowledgeBase
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'AI request failed');
      }

      // Add to conversation history with user request
      const chatEntry = {
        ...data,
        userRequest: userRequest // Store user's message
      };
      setResponseHistory(prev => [...prev, chatEntry]);
      setAiResponse(data);

      // Clear user request for next input (like a chat)
      setUserRequest('');

      // Detect response type and handle accordingly
      const isModification = data.modifications?.updatedScript;

      if (isModification) {
        // MODIFICATION - Store for confirmation, don't auto-apply
        setPendingModification({
          script: data.modifications.updatedScript,
          explanation: data.modifications.explanation || 'Code modified',
          changedLines: data.modifications.changedLines || []
        });
        
        logger.debug('[AI-ASSISTANT] Modification ready (pending user confirmation)');
      } else {
        // ANALYSIS - Just chat response, no code changes
        setPendingModification(null);
        logger.debug('[AI-ASSISTANT] Analysis response (no code changes)');
      }

      // ✅ Handle pre-parsed steps if available
      if (data.preParsedSteps && Array.isArray(data.preParsedSteps)) {
        logger.debug(`[AI-ASSISTANT] Received ${data.preParsedSteps.length} pre-parsed steps (parsing method: ${data.parsingMethod})`);
        
        // Show parsing indicator briefly
        setIsParsing(true);
        setTimeout(() => setIsParsing(false), 500);
        
        // Pass steps to parent via callback
        if (onStepsUpdate) {
          onStepsUpdate(data.preParsedSteps);
        }
      } else {
        logger.info('ℹ️ [AI-ASSISTANT] No pre-parsed steps (parsing will happen on save)');
      }

      // ✅ Pass description to parent via callback
      if (data.description && onDescriptionUpdate) {
        onDescriptionUpdate(data.description);
        logger.debug(`[AI-ASSISTANT] Passing AI description to parent (${data.description.length} chars)`);
      }

    } catch (error: any) {
      logger.error('AI request failed:', { error: getErrorMessage(error) });
      setError(error.message || 'Failed to process AI request');
    } finally {
      setIsProcessing(false);
    }
  };

  // Skip rendering if modal mode and not open
  if (mode === 'modal' && !isOpen) return null;

  const contentUI = (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ height: mode === 'embedded' ? '100%' : 'auto' }}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              AI Assistant
            </h2>
          </div>
          {mode === 'modal' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={closeAI}
              icon={<X className="w-4 h-4" />}
              ariaLabel="Close AI Assistant"
            >
              {''}
            </Button>
          )}
        </div>

        {/* Context Info */}
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Context: <span className="font-medium text-gray-900 dark:text-white">
              {context?.screenName || 'Unknown'}
            </span>
            {context?.flowName && (
              <span className="text-gray-600 dark:text-gray-400"> - "{context.flowName}"</span>
            )}
          </p>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden p-3 flex flex-col">
          {!isInitialized ? (
            // STAGE 1: LAZY LOAD - Blur Screen with "Use AI" Button
            <div className="flex flex-col items-center justify-center min-h-[300px] bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 backdrop-blur-sm rounded-lg">
              <Sparkles className="w-10 h-10 text-purple-600 dark:text-purple-400 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                AI Assistant
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center px-4">
                Click to activate AI assistance
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={handleUseAI}
                disabled={isInitializing}
                loading={isInitializing}
                icon={<Sparkles className="w-4 h-4" />}
                iconPosition="left"
                className="!bg-gradient-to-r !from-purple-600 !to-blue-600 hover:!from-purple-700 hover:!to-blue-700 shadow-lg"
              >
                {isInitializing ? 'Activating...' : 'Use AI'}
              </Button>
            </div>
          ) : (
            // STAGE 2: CHAT-STYLE UI
            <div className="flex flex-col h-full">

              {/* Conversation Area - Scrollable Chat */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                {responseHistory.length > 0 ? (
                  <>
                    {responseHistory.map((response, index) => (
                      <div key={index} className="space-y-2">
                        
                        {/* User Message Bubble */}
                        <div className="flex justify-end">
                          <div className="max-w-[85%] bg-purple-600 text-white rounded-lg px-3 py-2">
                            <p className="text-xs font-medium">You</p>
                            <p className="text-sm">{(response as any).userRequest || 'Requested changes'}</p>
                          </div>
                        </div>

                        {/* AI Response Bubble */}
                        <div className="flex justify-start">
                          <div className="max-w-[85%] space-y-2">
                            {/* Response Badge */}
                            <div className="flex items-center gap-2 mb-1">
                              <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                              <span className="text-xs font-medium text-gray-900 dark:text-white">AI Assistant</span>
                              {response.modifications?.updatedScript ? (
                                <Badge variant="warning">Code Changes</Badge>
                              ) : (
                                <Badge variant="info">Analysis</Badge>
                              )}
                            </div>
                            
                            {/* Response Content */}
                            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2">
                              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {response.analysis}
                              </p>
                              
                              {/* Show modification details if present */}
                              {response.modifications?.explanation && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                  <span className="font-medium">Changes:</span> {response.modifications.explanation}
                                </p>
                              )}
                              
                              {response.modifications?.changedLines && response.modifications.changedLines.length > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  Modified lines: {response.modifications.changedLines.join(', ')}
                                </p>
                              )}
                            </div>
                            
                            {/* Accept/Reject Buttons (Only for latest response with pending modification) */}
                            {index === responseHistory.length - 1 && pendingModification && (
                              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                                <p className="text-xs font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                                  📝 Code Changes Ready
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                                  {pendingModification.explanation}
                                </p>
                                <ButtonGroup spacing="sm" attached={false}>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                      if (onUpdate && pendingModification) {
                                        onUpdate(pendingModification.script);
                                        setPendingModification(null);
                                      }
                                    }}
                                    className="!bg-green-600 hover:!bg-green-700 !text-white"
                                  >
                                    ✓ Accept Changes
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setPendingModification(null);
                                    }}
                                  >
                                    ✗ Reject
                                  </Button>
                                </ButtonGroup>
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    ))}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-center p-4">
                    <div>
                      <Sparkles className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Start a conversation with AI
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed Input Area at Bottom */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-900 dark:text-red-200">{error}</p>
                    </div>
                  </div>
                )}

                {/* Dropdowns - Side by Side */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      AI Model
                    </label>
                    <Dropdown
                      label=""
                      options={availableProviders.map(m => ({
                        value: m.value,
                        label: m.label
                      }))}
                      value={provider}
                      onChange={(value) => setProvider(value as string)}
                      disabled={isProcessing}
                      placeholder="Select AI model"
                      className="text-xs"
                      maxHeight="max-h-64"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Knowledge Base
                    </label>
                    <Dropdown
                      label=""
                      options={knowledgeBases.map(kb => ({
                        value: kb.id,
                        label: kb.name
                      }))}
                      value={knowledgeBase}
                      onChange={(value) => setKnowledgeBase(value as string | null)}
                      disabled={isProcessing || isLoadingKBs}
                      placeholder={isLoadingKBs ? "Loading..." : "Select KB"}
                      className="text-xs"
                      maxHeight="max-h-32"
                    />
                    {/* KB Load Error with Retry */}
                    {kbLoadError && (
                      <div className="text-xs mt-1">
                        <p className="text-red-600 dark:text-red-400">{kbLoadError}</p>
                        <button
                          onClick={() => fetchKnowledgeBases()}
                          className="text-blue-600 dark:text-blue-400 hover:underline mt-1"
                          disabled={isLoadingKBs}
                        >
                          {isLoadingKBs ? 'Retrying...' : 'Retry'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message Input with Send Button */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={userRequest}
                    onChange={(e) => setUserRequest(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && userRequest.trim() && !isProcessing) {
                        e.preventDefault();
                        handleActivateAI();
                      }
                    }}
                    placeholder="Type your request..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    disabled={isProcessing}
                  />
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleActivateAI}
                    disabled={isProcessing || !userRequest.trim()}
                    loading={isProcessing}
                    icon={<Sparkles className="w-4 h-4" />}
                    ariaLabel="Send AI Request"
                    className="!bg-gradient-to-r !from-purple-600 !to-blue-600 hover:!from-purple-700 hover:!to-blue-700"
                  >
                    {''}
                  </Button>
                </div>

                {/* ✅ NEW: Parsing Indicator */}
                {isParsing && (
                  <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    <span>Parsing script...</span>
                  </div>
                )}

                {/* Clear History Button */}
                {responseHistory.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setResponseHistory([]);
                      setAiResponse(null);
                    }}
                    fullWidth
                    className="!text-xs !text-gray-500 hover:!text-red-600 dark:!text-gray-400 dark:hover:!text-red-400"
                  >
                    Clear Chat History ({responseHistory.length})
                  </Button>
                )}

              </div>

            </div>
          )}
        </div>

      </div>
  );

  // Embedded mode - render inline with custom width
  if (mode === 'embedded') {
    return (
      <div style={{ width, height: '100%' }} className="flex flex-col">
        {contentUI}
      </div>
    );
  }

  // Modal mode - render as portal with backdrop
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh]">
        {contentUI}
      </div>
    </div>,
    document.body
  );
};

